import { Toast as BaseToast } from '@base-ui/react/toast';
import {
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiCloseLargeLine,
  RiInformationLine,
  RiLoader2Fill,
  RiSpamLine,
} from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { buttonStyles } from './button';
import { usePortalContainer, type PortalContainer } from './lib/portal';

export type ToastType = 'default' | 'success' | 'info' | 'warning' | 'error' | 'loading';

export interface ToastOptions {
  type?: ToastType;
  title: React.ReactNode;
  /** Second line (400 12/16 White/60). */
  subtitle?: React.ReactNode;
  /** Small Secondary button. */
  action?: { label: React.ReactNode; onClick: () => void };
  /** ms before auto-dismiss; 0 keeps it until closed. Default 4000 (loading: 0). */
  duration?: number;
  /** Replace an existing toast with the same id. */
  id?: string;
  priority?: 'low' | 'high';
  onClose?: () => void;
}

/** One manager for the whole app — call `toast.*` from anywhere (also outside React). */
const manager = BaseToast.createToastManager();

function add(options: ToastOptions): string {
  const type = options.type ?? 'default';
  return manager.add({
    id: options.id,
    type,
    title: options.title,
    description: options.subtitle,
    timeout: options.duration ?? (type === 'loading' ? 0 : 4000),
    priority: options.priority ?? (type === 'error' ? 'high' : 'low'),
    onClose: options.onClose,
    actionProps: options.action ? { children: options.action.label, onClick: options.action.onClick } : undefined,
  });
}

type ShortOptions = Omit<ToastOptions, 'type' | 'title'>;

/** Toast API: `toast.success('Saqlandi')`, `toast.error('Xatolik yuz berdi', { subtitle })`, `toast.promise(p, …)`. */
export const toast = Object.assign((title: React.ReactNode, options?: ShortOptions) => add({ ...options, title }), {
  show: add,
  success: (title: React.ReactNode, options?: ShortOptions) => add({ ...options, title, type: 'success' }),
  error: (title: React.ReactNode, options?: ShortOptions) => add({ ...options, title, type: 'error' }),
  info: (title: React.ReactNode, options?: ShortOptions) => add({ ...options, title, type: 'info' }),
  warning: (title: React.ReactNode, options?: ShortOptions) => add({ ...options, title, type: 'warning' }),
  loading: (title: React.ReactNode, options?: ShortOptions) => add({ ...options, title, type: 'loading' }),
  /** Update a toast in place (e.g. loading → success). */
  update: (id: string, options: Partial<ToastOptions>) =>
    manager.update(id, {
      ...(options.type ? { type: options.type } : null),
      ...(options.title !== undefined ? { title: options.title } : null),
      ...(options.subtitle !== undefined ? { description: options.subtitle } : null),
      ...(options.duration !== undefined || options.type
        ? { timeout: options.duration ?? (options.type === 'loading' ? 0 : 4000) }
        : null),
    }),
  dismiss: (id?: string) => manager.close(id),
  /** Loading toast that turns into success / error when the promise settles. */
  promise: <T,>(
    promise: Promise<T>,
    messages: { loading: React.ReactNode; success: React.ReactNode | ((value: T) => React.ReactNode); error: React.ReactNode | ((error: unknown) => React.ReactNode) },
  ): Promise<T> => {
    const id = add({ type: 'loading', title: messages.loading });
    promise.then(
      (value) =>
        toast.update(id, { type: 'success', title: typeof messages.success === 'function' ? messages.success(value) : messages.success }),
      (error: unknown) =>
        toast.update(id, { type: 'error', title: typeof messages.error === 'function' ? messages.error(error) : messages.error }),
    );
    return promise;
  },
});

const TYPE_STYLE: Record<ToastType, { icon?: React.ReactNode; title: string }> = {
  default: { title: 'text-fg' },
  success: { icon: <RiCheckboxCircleLine aria-hidden className="size-20 shrink-0 text-tone-green" />, title: 'text-tone-green' },
  info: { icon: <RiInformationLine aria-hidden className="size-20 shrink-0 text-tone-blue" />, title: 'text-tone-blue' },
  warning: { icon: <RiSpamLine aria-hidden className="size-20 shrink-0 text-tone-peach" />, title: 'text-tone-peach' },
  error: { icon: <RiCloseCircleLine aria-hidden className="size-20 shrink-0 text-tone-red-lighter" />, title: 'text-tone-red-lighter' },
  loading: { icon: <RiLoader2Fill aria-hidden className="size-20 shrink-0 animate-spin text-icon" />, title: 'text-fg' },
};

function ToastList({ closeLabel }: { closeLabel: string }) {
  const { toasts } = BaseToast.useToastManager();
  return toasts.map((t) => {
    const style = TYPE_STYLE[(t.type as ToastType | undefined) ?? 'default'] ?? TYPE_STYLE.default;
    return (
      <BaseToast.Root
        key={t.id}
        toast={t}
        className={cn(
          'pointer-events-auto flex w-432 max-w-full items-center gap-12 rounded-4 border border-line bg-panel px-12 py-8 text-fg shadow-toast',
          '[transform:translate(var(--toast-swipe-movement-x),var(--toast-swipe-movement-y))] transition-[opacity,transform,translate] duration-200 ease-standard',
          'data-starting-style:translate-y-8 data-starting-style:opacity-0 data-ending-style:opacity-0 data-limited:hidden',
        )}
      >
        {style.icon}
        <div className="flex min-w-0 flex-1 flex-col">
          <BaseToast.Title className={cn('text-sm font-semibold', style.title)} />
          <BaseToast.Description className="text-xs text-fg-secondary empty:hidden" />
        </div>
        {t.actionProps && <BaseToast.Action className={buttonStyles({ size: 'sm', variant: 'secondary' }).base()} />}
        <BaseToast.Close
          aria-label={closeLabel}
          className="flex size-24 shrink-0 cursor-pointer items-center justify-center rounded-full text-icon transition-colors hover:bg-hover hover:text-fg"
        >
          <RiCloseLargeLine aria-hidden className="size-16" />
        </BaseToast.Close>
      </BaseToast.Root>
    );
  });
}

export interface ToasterProps {
  /** `bottom-center` (default, 03-modules §2.5) or `bottom-right`. */
  position?: 'bottom-center' | 'bottom-right' | 'top-center';
  /** Max visible toasts. Default 3. */
  limit?: number;
  closeLabel?: string;
  container?: PortalContainer;
}

/** Mount once near the app root. Renders the toasts queued with `toast.*` (kit Toast 67:12191). */
export function Toaster({ position = 'bottom-center', limit = 3, closeLabel = 'Yopish', container }: ToasterProps) {
  const portalContainer = usePortalContainer(container);
  return (
    <BaseToast.Provider toastManager={manager} limit={limit}>
      <BaseToast.Portal container={portalContainer}>
        <BaseToast.Viewport
          className={cn(
            'pointer-events-none fixed z-80 flex max-w-[calc(100vw-2rem)] gap-8 outline-none',
            position === 'bottom-center' && 'bottom-24 left-1/2 -translate-x-1/2 flex-col-reverse items-center',
            position === 'bottom-right' && 'right-24 bottom-24 flex-col-reverse items-end',
            position === 'top-center' && 'top-24 left-1/2 -translate-x-1/2 flex-col items-center',
          )}
        >
          <ToastList closeLabel={closeLabel} />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  );
}
